import { useCallback, useRef, useState } from 'react';
import shortsMediaAPI from '../services/fetcher/shorts';

export const useShortsMedia = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [shortsMediaList, setShortsMediaList] = useState<ShortsMediaContentsDetailResDto[]>([]);

  const isFetchingRef = useRef<boolean>(false);
  const shortsMediaIdsRef = useRef<string[]>([]);

  // 배열 랜덤 셔플 함수
  const shuffleArray = <T>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // 숏폼 목록 조회 (상세 정보 없이 ID만 가져오기)
  const fetchShortsMediaList = useCallback(async (): Promise<ShortsMediaContentsDetailResDto[]> => {
    // 이미 로딩 중이면 중복 호출 방지
    if (isFetchingRef.current) {
      return []; // 빈 배열 반환으로 변경
    }

    try {
      isFetchingRef.current = true;
      setIsLoading(true);
      
      const result = await shortsMediaAPI.getShortsMediaList();
      
      // 고정 영상을 orderNum에 따라 정렬
      const sortedStaticList = [...result.staticDisplayShortsMediaList].sort((a, b) => {
        // orderNum이 없는 경우를 대비해 기본값 처리
        const orderA = a.orderNum !== undefined ? a.orderNum : Number.MAX_SAFE_INTEGER;
        const orderB = b.orderNum !== undefined ? b.orderNum : Number.MAX_SAFE_INTEGER;
        return orderA - orderB; // 오름차순 정렬
      });

      // dynamicDisplayShortsMediaList를 랜덤으로 섞기
      const shuffledDynamicList = shuffleArray(result.dynamicDisplayShortsMediaList);

      // 모든 아이템의 ID만 수집 (정렬된 고정 영상 최대 5개 + 랜덤 영상 최대 50개)
      const allItems = [
        ...sortedStaticList.slice(0, 5), // 정렬된 고정 영상 최대 5개
        ...shuffledDynamicList.slice(0, 50) // 랜덤 영상 최대 50개
      ];
      
      // API 호출은 성공했지만 데이터가 없는 경우 빈 배열 반환
      if (allItems.length === 0) {
        setShortsMediaList([]);
        shortsMediaIdsRef.current = [];
        return [];
      }
      
      // ID 목록 저장
      shortsMediaIdsRef.current = allItems.map(item => item.shortsMediaId);
      
      // 초기에는 빈 배열로 설정 (상세 정보는 필요할 때 로드)
      setShortsMediaList([]);
      return [];
      
    } catch (error) {
      console.error('숏폼 목록 조회 실패:', error);
      throw error;
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, []); // ✅ 의존성 배열에서 shortsMediaList 제거

  // 개별 숏폼 상세 정보 조회
  const fetchShortsMediaDetail = useCallback(async (shortsMediaId: string): Promise<ShortsMediaContentsDetailResDto | null> => {
    
    try {
      return await shortsMediaAPI.getShortsMediaDetail(shortsMediaId);
    } catch (error) {
      console.warn(`상세 정보 조회 실패: ${shortsMediaId}`, error);
      return null;
    }
  }, []);

  // 배치로 숏폼 상세 정보 조회 (초기 로드용)
  const fetchShortsMediaDetailsBatch = useCallback(async (shortsMediaIds: string[]): Promise<ShortsMediaContentsDetailResDto[]> => {
    const batchSize = 5; // 한 번에 5개씩 처리
    const results: ShortsMediaContentsDetailResDto[] = [];
    const timeouts: NodeJS.Timeout[] = []; // ✅ timeout 참조 저장
    
    try {
      for (let i = 0; i < shortsMediaIds.length; i += batchSize) {
        const batch = shortsMediaIds.slice(i, i + batchSize);
        const batchPromises = batch.map(id => fetchShortsMediaDetail(id));
        const batchResults = await Promise.all(batchPromises);
        
        const validResults = batchResults.filter((result): result is ShortsMediaContentsDetailResDto => result !== null);
        results.push(...validResults);
        
        // 배치 간 약간의 지연 (서버 부하 방지) - timeout 참조 저장
        if (i + batchSize < shortsMediaIds.length) {
          await new Promise(resolve => {
            const timeout = setTimeout(resolve, 100);
            timeouts.push(timeout); // ✅ timeout 참조 저장
          });
        }
      }
      
      return results;
    } catch (error) {
      // ✅ 에러 발생 시 모든 timeout 정리
      timeouts.forEach(timeout => clearTimeout(timeout));
      throw error;
    }
  }, [fetchShortsMediaDetail]);

  // 숏폼 좋아요 - 최적화된 버전
  const likeShortsMedia = useCallback(async (shortsMediaId: string) => {
    try {
      return await shortsMediaAPI.addShortsMediaLike(shortsMediaId);
    } catch (error) {
      console.error('숏폼 좋아요 실패:', error);
      throw error;
    }
  }, []);

  /**
   * 풀영상 상세 정보 조회
   */
  const getFullVersionContentsDetailInfo = useCallback(async (mediaId: string) => {
    if (!mediaId) {
      console.warn('풀버전 정보 조회: mediaId가 없습니다');
      return null;
    }

    try {
      const result = await shortsMediaAPI.getLongFormMediaDetail(mediaId);

      if (!result) {
        console.warn(`풀버전 정보 조회: ${mediaId}에 대한 결과가 없습니다`);
        return null;
      }

      // 결과 검증
      if (!result.title) {
        console.warn(`풀버전 정보 조회: ${mediaId}에 대한 결과에 title이 없습니다`, result);
      }
      return result;
    } catch (error) {
      console.error(`풀버전 정보 조회 실패: ${mediaId}`, error);
      return null; // 에러 시 null 반환
    }
  }, []);

  return {
    isLoading,
    shortsMediaList,
    fetchShortsMediaList,
    fetchShortsMediaDetail,
    fetchShortsMediaDetailsBatch,
    shortsMediaIds: shortsMediaIdsRef.current,
    likeShortsMedia,
    getFullVersionContentsDetailInfo,
  };
}; 